import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography, useTheme,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import useColumnPreferences from '../pro-table/useColumnPreferences';
import ProTablePreferences from '../pro-table/ProTablePreferences';
import { QUADRANTS, STAGES, getHealth, getPotential, getQuadrant, getStage, nextAction } from '../../utils/platformKpi';
import { HEALTH_COLORS, POTENTIAL_LABELS, Section, formatBytes, formatCount, planLabel } from './common';

export const COLUMNS = [
  { key: 'center', label: 'Center' },
  { key: 'plan', label: 'Plan' },
  { key: 'stage', label: 'Stage' },
  { key: 'potential', label: 'Potential' },
  { key: 'clients', label: 'Clients', align: 'right' },
  { key: 'employees', label: 'Employees', align: 'right' },
  { key: 'health', label: 'Health' },
  { key: 'lastBusiness', label: 'Last business activity' },
  { key: 'storage', label: 'Storage', align: 'right' },
  { key: 'nextAction', label: 'Next action' },
];

const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));
const HEALTH_LABEL = { red: 'At risk', amber: 'Quiet', green: 'Healthy' };
const HEALTH_RANK = { red: 0, amber: 1, green: 2 };
const POTENTIAL_RANK = { high: 0, medium: 1, low: 2 };

const filterLabel = (f) => {
  if (!f) return null;
  if (f.type === 'quadrant') return `${QUADRANTS[f.value].label} centers`;
  if (f.type === 'stage') return `Stage: ${STAGE_LABEL[f.value]}`;
  return HEALTH_LABEL[f.value];
};

const relativeDays = (iso) => {
  if (!iso) return 'Never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'Today';
  return days === 1 ? 'Yesterday' : `${days} days ago`;
};

/** Every center with its plan, stage, potential and health (design §4.7). */
const CentersTable = ({ centers, filter, onClearFilter, onOpen, source }) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const prefs = useColumnPreferences('platformKpi.centers', COLUMNS);

  useEffect(() => { setPage(0); }, [filter, centers]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return centers
      .map((c) => ({ c, stage: getStage(c), potential: getPotential(c), health: getHealth(c), quadrant: getQuadrant(c) }))
      .filter((r) => !filter
        || (filter.type === 'quadrant' && r.potential != null && r.quadrant === filter.value)
        || (filter.type === 'stage' && r.stage === filter.value)
        || (filter.type === 'health' && r.health.level === filter.value))
      .filter((r) => !q || r.c.name?.toLowerCase().includes(q) || r.c.code?.toLowerCase().includes(q))
      .sort((a, b) => HEALTH_RANK[a.health.level] - HEALTH_RANK[b.health.level]
        || (POTENTIAL_RANK[a.potential] ?? 3) - (POTENTIAL_RANK[b.potential] ?? 3)
        || a.c.name.localeCompare(b.c.name));
  }, [centers, filter, search]);

  const pageRows = rows.slice(page * 10, page * 10 + 10);

  const cell = (key, r) => {
    const { c } = r;
    switch (key) {
      case 'center':
        return (
          <Box>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{c.name}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{c.code}{c.active ? '' : ' · disabled'}</Typography>
          </Box>
        );
      case 'plan':
        return planLabel(c.plan);
      case 'stage': return STAGE_LABEL[r.stage];
      case 'potential': return r.potential ? POTENTIAL_LABELS[r.potential] : '—';
      case 'clients': return formatCount(c.clients);
      case 'employees': return formatCount(c.employees);
      case 'health':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: HEALTH_COLORS[r.health.level] }} />
            {HEALTH_LABEL[r.health.level]}
          </Box>
        );
      case 'lastBusiness': return relativeDays(c.lastBusinessAt);
      case 'storage': return formatBytes(c.storageBytes);
      case 'nextAction': return <Typography sx={{ fontSize: '0.75rem', color: 'primary.main', minWidth: 150, maxWidth: 220 }}>{nextAction(c)}</Typography>;
      default: return null;
    }
  };

  return (
    <Section
      title={`Centers (${rows.length})`}
      source={source}
      action={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {filter && <Chip size="small" label={filterLabel(filter)} onDelete={onClearFilter} color="primary" />}
          <TextField
            size="small"
            placeholder="Search center or code"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ width: { xs: 160, sm: 220 }, '& .MuiInputBase-root': { height: 32, fontSize: '0.8125rem' } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <ProTablePreferences columns={COLUMNS} preferences={prefs} />
        </Box>
      )}
    >
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={getBrandedTableHeadSx(theme)}>
              {prefs.visibleColumns.map((col) => (
                <TableCell key={col.key} align={col.align} sx={{ fontSize: '0.75rem', fontWeight: 600, py: 1, whiteSpace: 'nowrap' }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((r) => (
              <TableRow key={r.c.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(r.c)}>
                {prefs.visibleColumns.map((col) => (
                  <TableCell key={col.key} align={col.align} sx={{ fontSize: '0.8125rem', py: 0.75, whiteSpace: col.key === 'nextAction' ? 'normal' : 'nowrap' }}>
                    {cell(col.key, r)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={prefs.visibleColumns.length} sx={{ textAlign: 'center', py: 3, color: 'text.secondary', fontSize: '0.8125rem' }}>
                  No centers match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > 10 && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
          onPageChange={(_, p) => setPage(p)}
        />
      )}
    </Section>
  );
};

export default CentersTable;
